import { useCallback, useContext, useEffect, useState } from "react";
import { fetchStreamResponse, parseStreamResponse } from "@/lib/utils";
import { ChatsContext, CurrentChatContext } from "@/components/context";
import { AssistantAvatar } from "@/components/ui/assistantavatar";
import Markdown from "react-markdown";
import { useMsal } from "@azure/msal-react";
import { getJWT } from "@/lib/sso";

interface StreamingResponseProps {
    bottomOfPage: HTMLDivElement | null;
}

export const StreamingResponse = ({ bottomOfPage }: StreamingResponseProps) => {
    const { currentChat, updateCurrentChat } = useContext(CurrentChatContext);
    const { updateChat } = useContext(ChatsContext);
    const { instance, accounts } = useMsal();
    const [response, setResponse] = useState<string | undefined>(undefined);
    const lastMessageRole =
        currentChat?.messages && currentChat.messages.length > 0
            ? currentChat.messages[currentChat.messages.length - 1].role
            : undefined;
    const messageStr = JSON.stringify({ messages: currentChat?.messages });

    const addToChat = useCallback(
        async (message: string) => {
            if (currentChat) {
                const newChat = { ...currentChat };
                newChat.timestamp = Date.now();
                newChat.messages.push({
                    content: [{ text: message }],
                    role: "assistant",
                });
                updateCurrentChat(newChat);
                // Update the chats in state and local storage
                await updateChat(newChat);
            }
        },
        [currentChat, updateCurrentChat, updateChat]
    );

    useEffect(() => {
        bottomOfPage?.scrollIntoView({ behavior: "instant", block: "end" });
    }, [response, bottomOfPage]);

    useEffect(() => {
        const abortController = new AbortController();
        const handleStream = async () => {
            // When the API starts responding, add that to the current response section of the page.
            setResponse("[Thinking...]");
            const jwt = await getJWT(instance, accounts);
            const stream = await fetchStreamResponse(messageStr, jwt || "", abortController.signal);
            if (stream) {
                const parser = parseStreamResponse(stream);
                let next = await parser.next();
                let responseStr = "";
                while (!next.done) {
                    const line = next.value;
                    // console.log(line);
                    if (line?.contentBlockDelta) {
                        responseStr += line.contentBlockDelta.delta?.text;
                        // While the API is still responding, add that to the current response section of the page.
                        setResponse(responseStr);
                    } else if (line?.error) {
                        // If there is an error, add that to the current response section of the page.
                        responseStr += `[Sorry, there was an error: ${line.error}. Please try again.]`;
                        setResponse(responseStr);
                        // throw new Error(line.error);
                    }
                    next = await parser.next();
                }
                // When the API stops responding, add that response to the current chat (by ID) and remove the current response.
                setResponse(undefined);
                await addToChat(responseStr);
            }
        };
        // When the current chat length changes, if the last message is from the user, submit the chat to the API.
        if (lastMessageRole === "user") {
            // Fetch the streaming response
            handleStream()
                .then(() => {
                    // console.log(`StreamingResponse finished`);
                })
                .catch(async (err) => {
                    if (err.name !== 'AbortError') {
                        console.log(`StreamingResponse error: ${err}`);
                        setResponse(undefined);
                        await addToChat(`[Sorry, there was an error: ${err}. Please try again.]`);
                        throw err;
                    }
                });
        }
        return () => {
            // console.log(`StreamingResponse cleanup`);
            abortController.abort();
        };
    }, [accounts, addToChat, instance, lastMessageRole, messageStr]);

    return (
        <div className={"streaming-response"}>
            {response && (
                <div className="flex flex-row items-start">
                    <div className={"hidden lg:block lg:mr-[14px] lg:mt-2"}>
                        <AssistantAvatar size={24} animate={true} />
                    </div>
                    <div className="flex flex-col items-start">
                        <div className={"flex-auto"}>
                            <div
                                className={
                                    "bg-zinc-200 rounded-xl rounded-bl-none lg:rounded-bl-xl py-1.5 px-4 mb-4 prose prose-zinc text-md max-w-[calc(100vw-1rem)] lg:max-w-[65ch]"
                                }
                            >
                                <Markdown>{response}</Markdown>
                            </div>
                        </div>
                    </div>
                    <div className={"hidden lg:block lg:ml-[14px] lg:w-[24px] lg:h-1 lg:flex-none"}>
                        &nbsp;
                    </div>
                </div>
            )}
        </div>
    );
};
